import { IElementBaseModel } from '../../Interface/base/element-base-model.interface';
import { IGroupModel } from '../../Interface/group-model.interface';
import { enumerateGroup, enumerateGroupItems } from '../group-operation/enumerate';
import * as sdkToUI from '@src/slide/domain/ui-model/adapter/style-adapters/sdk-ui';
import { FillType, GradientType, IGradFill, IImageFill } from './fill.interface';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { UIModelType } from '../../constant';
import { IOffsetFrame } from '../../Interface/model-props/element-model-props.interface';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import { IFrame } from '../frame/frame.interface';
import { FillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/fill-converter';
import { ECSType, getChangeSetType } from '../../../utils/changeset-type';
import { ICTShapeProperties } from '@tencent/slide-sdk-pb-model';

export const HAS_GRP_FILL = 1; // 组合子元素继承组合填充属性
const DEFAULT_ROTATE = 0;
const NOT_ROTATE_WITH_SHAPE = 0;
const FLIP = 1;
const NOT_FLIP = 0;
const CHANGE_NUM = 1;

/**
 * 调整组合子元素的背景填充属性
 */
export default class AdjustGroupChildrenBg {
    private grpFillGroup: IGroupModel | null;

    public constructor(grpFillGroup?: IGroupModel) {
        this.grpFillGroup = grpFillGroup ?? null;
    }

    /**
     * 当组合有变更时，需要遍历子元素更新背景
     */
    public adjustChildBg(sdkBackground) {
        if (!this.grpFillGroup) return;
        const fillType = this.grpFillGroup.getBackgroundFill?.().getFillType();
        this.resetBackgroundRecursion(this.grpFillGroup, sdkBackground, fillType);
    }

    /**
     * 当子元素有变更时，需要遍历组合更新所有子元素的offsetFrame
     */
    public adjustChildOffsetFrame(childModel: IElementBaseModel, sdkData, changesetInfo) {
        // 仅继承了父组合填充属性的子元素需要调整
        if (!this.isGrpFill(childModel, sdkData)) return;
        if (!this.didFrameChange(childModel, changesetInfo)) return;
        // 仅渐变/图片/图案填充需要进行调整
        if (!this.isAjudstType(childModel.getBackgroundFill?.().getShownFillType())) return;
        const grpFillGroup = this.getGrpFillGroup(childModel);
        enumerateGroupItems(grpFillGroup, (model: IElementBaseModel) => {
            this.setOffsetFrame(model);
        });
        // 影响到其他子元素填充内容的情况下，需要触发组合重新渲染
        grpFillGroup.refreshGroupByChildrenWithoutChangeSet += CHANGE_NUM;
    }

    /**
     * 如果组合的背景发生了改变，则要递归重置所有子元素的背景
     */
    private resetBackgroundRecursion(uiData: IGroupModel, sdkBackground, fillType: FillType | undefined) {
        uiData.children.forEach((item: IElementBaseModel) => {
            this.adjustChildBackground(item, sdkBackground, fillType);
            if (item.type === UIModelType.GROUP) {
                this.resetBackgroundRecursion((item as IGroupModel), sdkBackground, fillType);
            }
        });
    }

    /**
     * 当子元素继承父元素填充属性时，对子元素进行矫正
     */
    private adjustChildBackground(
        childModel: IElementBaseModel, sdkBackground,
        fillType: FillType | undefined,
    ): void {
        const grpFill = (childModel.sdkData?.props?.spPr as ICTShapeProperties)?.grpFill;
        if (!grpFill) return;
        childModel.props.style.fillBackground = {};
        FillConverter.convertPartialDataFromSdkToUi(sdkBackground, childModel.props.style.fillBackground);
        childModel.props.style.fillBackground.grpFill = HAS_GRP_FILL;
        if (childModel.props.style.fillBackground.gradFill?.pathGradient?.path === GradientType.Shape) {
            childModel.props.style.fillBackground.gradFill.pathGradient.path = GradientType.Radial;
        }
        this.setOffsetFrame(childModel, fillType);
    }

    /**
     * 是否为frame发生变更
     */
    private didFrameChange(uiData, changesetInfo): boolean {
        // 目前仅组合子元素需要了解是否为frame发生变更
        if (uiData.parent?.type !== UIModelType.GROUP) return false;
        return getChangeSetType(changesetInfo) === ECSType.Frame;
    }

    /**
     * 获取子元素继承的填充属性的父组合
     * @param childModel
     * @returns
     */
    private getGrpFillGroup(childModel: IElementBaseModel): IGroupModel {
        if (this.grpFillGroup) return this.grpFillGroup;
        this.grpFillGroup = childModel.parent as IGroupModel;
        enumerateGroup(childModel, (groupModel: IGroupModel) => {
            const { fillBackground } = groupModel.props.style;
            if (!fillBackground?.grpFill) {
                this.grpFillGroup = groupModel;
                return true;
            }
        });
        return this.grpFillGroup;
    }

    /**
     * 获取子元素相对父组合的大小偏移值
     */
    private getOffsetFrame(childModel: IElementBaseModel): IOffsetFrame {
        const grpFillGroup = this.getGrpFillGroup(childModel);
        // 获取父组合的大小信息
        const grpFillGroupFrame = grpFillGroup.getFrame?.().frameOnSlide();
        let { left, top } = grpFillGroupFrame;
        let right = left + grpFillGroupFrame.width;
        let bottom = top + grpFillGroupFrame.height;
        const tempRotate = grpFillGroup.props.style.rotate ?? DEFAULT_ROTATE;
        const tempFlipH = grpFillGroup.props.style.flipH;
        const tempFlipV = grpFillGroup.props.style.flipV;
        const isRotateWithShape = this.isRotateWithShape(grpFillGroup);
        this.handleRotateWithShape(grpFillGroup, grpFillGroupFrame, isRotateWithShape);
        // 遍历子元素，获取包裹旋转后子元素的矩形的大小
        enumerateGroupItems(grpFillGroup, (childModel: IElementBaseModel) => {
            const frameOnSlide = childModel.getFrame?.().frameOnSlide();
            const frameAfterRotate = getFrameAfterRotate(frameOnSlide, frameOnSlide.rotate);
            left = Math.min(left, frameAfterRotate.left);
            top = Math.min(top, frameAfterRotate.top);
            right = Math.max(right, frameAfterRotate.left + frameAfterRotate.width);
            bottom = Math.max(bottom, frameAfterRotate.top + frameAfterRotate.height);
        }, [UIModelType.GROUP]);
        // 获取子元素相对包裹子元素矩形大小的偏移值
        const childFrameOnSlide = childModel.getFrame?.().frameOnSlide();
        const offsetWidth = right - left;
        const offsetHeight = bottom - top;
        const offsetX = left - childFrameOnSlide.left;
        const offsetY = top - childFrameOnSlide.top;
        let offsetRotateWithShape;
        if (isRotateWithShape) {
            const childRotate = (childFrameOnSlide.rotate || DEFAULT_ROTATE);
            const groupRotate = (grpFillGroup.getFrame?.().frameOnSlide().rotate || DEFAULT_ROTATE);
            offsetRotateWithShape = {
                offsetRotate: (childRotate - groupRotate + 360) % 360,
                offsetFlipH: !!(grpFillGroupFrame.flipH),
                offsetFlipV: !!(grpFillGroupFrame.flipV),
            };
            grpFillGroup.props.style.rotate = tempRotate;
            grpFillGroup.props.style.flipH = tempFlipH;
            grpFillGroup.props.style.flipV = tempFlipV;
        }
        return {
            offsetX, offsetY, offsetWidth, offsetHeight,
            ...offsetRotateWithShape,
        };
    }


    /**
     * 当父组合设置跟随形状旋转时
     * 如果组合本身有旋转/翻转，需要将组合暂时置为不旋转/不翻转来计算子元素相对组合的frame信息
     */
    private handleRotateWithShape(
        grpFillGroup: IGroupModel, grpFillGroupFrame: IFrame,
        isRotateWithShape: boolean,
    ) {
        if (!isRotateWithShape) return;
        const { rotate = DEFAULT_ROTATE, flipH, flipV } = grpFillGroupFrame;
        const tempRotate = grpFillGroup.props.style.rotate ?? DEFAULT_ROTATE;
        grpFillGroup.props.style.rotate = tempRotate - rotate;
        if (grpFillGroupFrame.flipH) {
            grpFillGroup.props.style.flipH = flipH ? NOT_FLIP : FLIP;
        }
        if (grpFillGroupFrame.flipV) {
            grpFillGroup.props.style.flipV = flipV ? NOT_FLIP : FLIP;
        }
    }

    /**
     * 元素是否跟随形状旋转
     */
    private isRotateWithShape(model: IElementBaseModel): boolean {
        const currentFillInfo = model.getBackgroundFill().getCurrentFill()?.fillInfo;
        return !((currentFillInfo as IGradFill)?.rotateWithShape  === NOT_ROTATE_WITH_SHAPE
            || (currentFillInfo as IImageFill)?.rotWithShape === NOT_ROTATE_WITH_SHAPE);
    }

    /**
     * 子元素是否继承父组合填充
     */
    private isGrpFill(childModel: IElementBaseModel, sdkData = childModel.sdkData): boolean {
        const isGroupChildren = childModel.parent?.type === UIModelType.GROUP;
        if (!isGroupChildren) return false;
        const fillInfo = childModel.getBackgroundFill?.().getFillInfo();
        // 当子元素重新继承自组合，需要设置offsetFrame属性
        if (!fillInfo?.grpFill && (sdkData && this.hasSdkGrpFill(childModel))) {
            childModel.props.style.offsetFrame = this.getOffsetFrame(childModel);
            return true;
        }
        // 当子元素不再继承自组合，重置offsetFrame属性
        if (!fillInfo?.grpFill || (sdkData && !this.hasSdkGrpFill(childModel))) {
            childModel.props.style.offsetFrame = undefined;
            return false;
        }
        return true;
    }

    /**
     * 仅渐变/图片/图案填充需要进行调整
     */
    private isAjudstType(fillType?: FillType): boolean {
        return !!(fillType && [FillType.GradualFill, FillType.ImageFill, FillType.PattFill].includes(fillType));
    }

    private hasSdkGrpFill(childModel: IElementBaseModel): boolean {
        return !!(childModel.sdkData?.props?.spPr as ICTShapeProperties)?.grpFill;
    }

    private setOffsetFrame(childModel: IElementBaseModel, fillType?: FillType) {
        // 调整子元素的填充样式，通过修改offsetFrame调整子元素渲染的图形的内容
        if (!this.isGrpFill(childModel)) return;
        if (!this.isAjudstType(fillType ?? childModel.getBackgroundFill?.().getShownFillType())) return;
        childModel.props.style.offsetFrame = this.getOffsetFrame(childModel);
    }
}
